import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, Award, Clock, Users, CheckCircle, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TestimonialSlider from '../components/TestimonialSlider';
import ProcessSlider from '../components/ProcessSlider';
import PricingSection from '../components/PricingSection';
import { benefits, countryCodesList } from '../mock';
import { toast } from 'sonner';
import { fetchGeoLocation, submitLead, handleApiResponse } from '../services/apiService';

const Home = ({ onOpenPopup }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadGeoData = async () => {
      const data = await fetchGeoLocation();
      setGeoData(data);
      if (data?.data?.countryCode) {
        const countryItem = countryCodesList.find(c => c.country === data.data.countryName);
        if (countryItem) {
          setFormData(prev => ({ ...prev, countryCode: countryItem.code }));
        }
      }
    };
    loadGeoData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await submitLead(formData, geoData);
      const result = handleApiResponse(response);
      
      if (result.type === 'redirect') {
        toast.success(result.message);
        navigate(result.url);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', phone: '', countryCode: '+1', message: '' });
    }
  };
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Trademark Inspire - Professional Trademark Registration Services in USA | Protect Your Brand 2026</title>
        <meta name="description" content="Expert trademark registration services with 98% USPTO approval rate. Protect your brand with Trademark Inspire - 10,000+ successful registrations, transparent pricing, and 24/7 support. Register your trademark today!" />
        <meta name="keywords" content="trademark registration, trademark services, USPTO registration, brand protection, intellectual property, trademark attorney, register trademark USA" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://mobile-scroll-test-1.preview.emergentagent.com/" />
        <meta property="og:title" content="Trademark Inspire - Professional Trademark Registration Services" />
        <meta property="og:description" content="Expert trademark registration services with 98% USPTO approval rate. Protect your brand today!" />
        <meta property="og:url" content="https://mobile-scroll-test-1.preview.emergentagent.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white py-16 md:py-24 overflow-hidden">
        {/* Animated Geometric Shapes */}
        <div className="geometric-shape shape-1"></div>
        <div className="geometric-shape shape-2"></div>
        <div className="geometric-shape shape-3"></div>
        <div className="geometric-shape shape-4"></div>
        <div className="geometric-shape shape-5"></div>
        <div className="geometric-shape shape-6"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#F77F00] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#F77F00] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Content */}
            <div className="text-center md:text-left">
              <div className="inline-block mb-4 md:mb-6">
                <span className="bg-[#F77F00]/20 text-[#F77F00] px-4 py-2 rounded-full text-sm font-semibold border border-[#F77F00]/30">
                  ✓ Trusted by 10,000+ Businesses
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                Protect Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-100 to-[#F77F00]">
                  Brand Legacy
                </span>
              </h1>
              
              <p className="text-lg md:text-xl mb-6 text-gray-100 leading-relaxed">
                Professional trademark registration services to secure your intellectual property rights in the USA. Expert guidance from search to registration.
              </p>
              
              <div className="space-y-3 mb-6 md:mb-8">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">98% Success Rate with USPTO Approvals</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">10,000+ Trademarks Successfully Registered</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">Expert Legal Team with 24/7 Support</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">Transparent Pricing - No Hidden Fees</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center md:justify-start">
                <Button
                  onClick={() => {
                    if (window.openChatWidget) {
                      window.openChatWidget();
                    }
                  }}
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-6 md:px-8 py-4 md:py-5 text-base md:text-lg font-semibold"
                >
                  Chat Now
                </Button>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md mx-auto md:max-w-none w-full">
              <h2 className="text-2xl font-bold text-[#093336] mb-2">Register Your Trademark</h2>
              <p className="text-gray-600 mb-6">Get started today - fill out the form below</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="bg-white text-gray-900"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="bg-white text-gray-900"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                      <SelectTrigger className="w-[120px] bg-white text-gray-900">
                        <SelectValue placeholder="+1">
                          {formData.countryCode || "+1"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodesList.map((item) => (
                          <SelectItem key={item.id} value={item.code}>
                            {item.code} ({item.country})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                        setFormData({ ...formData, phone: value });
                      }}
                      placeholder="Phone number"
                      className="flex-1 bg-white text-gray-900"
                      pattern="[0-9]{10}"
                      title="Please enter a valid 10-digit phone number"
                      maxLength={10}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your trademark..."
                    rows={3}
                    className="bg-white text-gray-900"
                  />
                </div>
                
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#093336] hover:bg-[#0a4548] text-white py-6 text-lg font-semibold disabled:opacity-50">
                  {isSubmitting ? 'Submitting...' : 'Register My Trademark'}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Informative Section Below Hero */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl md:text-4xl font-bold text-[#093336] mb-6">
                  Why Trademark Protection Matters
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                  Your brand is your most valuable business asset. In today's competitive marketplace, protecting your trademark is essential to safeguard your reputation, prevent confusion, and maintain your unique market position.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                  A registered trademark gives you exclusive rights to use your brand name, logo, or slogan in connection with your products or services. It provides legal protection against infringement and adds significant value to your business.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700"><strong>Exclusive Rights:</strong> Prevents others from using similar marks in your industry</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700"><strong>Brand Value:</strong> Increases your business worth and market credibility</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700"><strong>Legal Protection:</strong> Provides nationwide protection and legal recourse</p>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85" 
                  alt="Trademark Protection" 
                  className="rounded-2xl shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#093336] mb-4">Why Choose Trademark Inspire?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive trademark services backed by years of expertise and a commitment to your success.
            </p>
          </div>
          
          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mobile-scroll-container md:flex-none">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 mobile-scroll-item">
                <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-[#093336] to-[#0a4548] text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <Shield size={48} className="mx-auto mb-4 text-[#F77F00]" />
              <div className="text-4xl font-bold mb-2">10,000+</div>
              <div className="text-gray-200">Trademarks Protected</div>
            </div>
            <div className="text-center">
              <Award size={48} className="mx-auto mb-4 text-[#F77F00]" />
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-gray-200">Success Rate</div>
            </div>
            <div className="text-center">
              <Clock size={48} className="mx-auto mb-4 text-[#F77F00]" />
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-gray-200">Expert Support</div>
            </div>
            <div className="text-center">
              <Users size={48} className="mx-auto mb-4 text-[#F77F00]" />
              <div className="text-4xl font-bold mb-2">5,000+</div>
              <div className="text-gray-200">Happy Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <div id="how-it-works">
        <ProcessSlider />
      </div>

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#093336] mb-4">Our Comprehensive Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Beyond trademark registration, we offer a full suite of legal and corporate services.
            </p>
          </div>
          
          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mobile-scroll-container md:flex-none">
            <Link to="/services/register-trademark" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer mobile-scroll-item">
              <img src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f" alt="Trademark Services" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#093336]/90 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[#F77F00] transition-colors">Trademark Services</h3>
                  <p className="text-gray-200">Complete trademark registration and protection services</p>
                </div>
              </div>
            </Link>
            
            <Link to="/services/register-copyright" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer mobile-scroll-item">
              <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85" alt="Copyright Services" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#093336]/90 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[#F77F00] transition-colors">Copyright Services</h3>
                  <p className="text-gray-200">Protect your creative works and intellectual property</p>
                </div>
              </div>
            </Link>
            
            <Link to="/services/patent-application" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer mobile-scroll-item">
              <img src="https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33" alt="Patent Services" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#093336]/90 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[#F77F00] transition-colors">Patent Services</h3>
                  <p className="text-gray-200">Secure patents for your innovative inventions</p>
                </div>
              </div>
            </Link>
            
            <Link to="/services/immigration-law" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer mobile-scroll-item">
              <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40" alt="Legal Services" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#093336]/90 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[#F77F00] transition-colors">Legal Services</h3>
                  <p className="text-gray-200">Expert legal counsel for your business needs</p>
                </div>
              </div>
            </Link>
            
            <Link to="/services/llc" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer mobile-scroll-item">
              <img src="https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg" alt="Company Formation" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#093336]/90 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[#F77F00] transition-colors">Company Formation</h3>
                  <p className="text-gray-200">LLC, Corporation, and Non-Profit formation services</p>
                </div>
              </div>
            </Link>
            
            <Link to="/services/bookkeeping" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer mobile-scroll-item">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab" alt="Corporate Services" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#093336]/90 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-[#F77F00] transition-colors">Corporate Services</h3>
                  <p className="text-gray-200">Comprehensive business and administrative services</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(247, 127, 0, 0.4) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(247, 127, 0, 0.3) 0%, transparent 50%)' }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-4">
              <span className="bg-[#F77F00]/20 text-[#F77F00] px-4 py-2 rounded-full text-sm font-semibold border border-[#F77F00]/30">
                ⚡ Act Now - Limited Time Offer
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
            <p className="text-xl md:text-2xl text-gray-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              Don't wait until it's too late. Secure your trademark today and protect your business for tomorrow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={onOpenPopup}
                className="bg-[#F77F00] hover:bg-[#e57300] text-white px-10 py-7 text-lg font-semibold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
              >
                Get Started Now <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
            <p className="text-sm text-gray-300 mt-6">✓ No Credit Card Required  •  ✓ Free Consultation  •  ✓ 98% Success Rate</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
